import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  Cancel01Icon,
  Delete02Icon,
  GitMergeIcon,
  InformationCircleIcon,
  Search01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { Permissions } from 'constants/permissions';
import { DEFAULT_TAG_COLOR, TAG_COLOR_PALETTE } from 'constants/tags';
import { useAccessControl } from 'hooks/access-control';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';

import Alert from 'components/Alert/Alert';
import { Button, Checkbox } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import { IconButton } from 'components/IconButton/IconButton';
import { Modal } from 'components/Modal/Modal';
import { CustomToast } from 'components/Toast/CustomToast';

import {
  bulkDeleteTags,
  createTag,
  deleteTag,
  getTags,
  mergeTags,
  selectTags,
  updateTag,
} from 'api/tags-slice';
import { selectPermissions } from 'api/user-permissions-slice';

import styles from './Tags.module.scss';

const colorEqual = (a, b) => (a || '').toLowerCase() === (b || '').toLowerCase();

const tagInUseToastMessage = tag => {
  const total = Number(tag.assignment_count ?? 0);
  const active = Number(tag.usage_count ?? 0);
  const archived = Number(tag.archived_usage_count ?? 0);
  const bits = [];
  if (active) bits.push(`${active} active`);
  if (archived) bits.push(`${archived} archived`);
  const breakdown = bits.length ? bits.join(', ') : `${total} candidate${total !== 1 ? 's' : ''}`;
  return `This tag is still assigned to ${breakdown} (${total} total). Remove it from every active or archived candidate, or merge it into another tag first.`;
};

export const Tags = ({ setLoading = () => {} }) => {
  const tags = useSelector(selectTags);
  const permissions = useSelector(selectPermissions);
  const dispatch = useDispatch();

  const hasAccess = useAccessControl(permissions);
  const canManage = hasAccess(Permissions.TagsWrite);

  // Search & sort
  const [search, setSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const searchWrapperRef = useRef(null);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Inline rename
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const editInputRef = useRef(null);

  // Inline description edit
  const [editingDescId, setEditingDescId] = useState(null);
  const [editingDesc, setEditingDesc] = useState('');
  const editDescInputRef = useRef(null);
  /** Set on Enter so the subsequent blur does not call save again (avoids duplicate updateTag) */
  const editDeferSaveRef = useRef(false);
  const editDescDeferSaveRef = useRef(false);

  // Inline create row
  const [showInlineCreate, setShowInlineCreate] = useState(false);
  const [inlineCreateName, setInlineCreateName] = useState('');
  const [inlineCreateDesc, setInlineCreateDesc] = useState('');
  const [inlineCreateColor, setInlineCreateColor] = useState(DEFAULT_TAG_COLOR);
  const [inlineCreateColorOpen, setInlineCreateColorOpen] = useState(false);
  const [inlineCreateSaving, setInlineCreateSaving] = useState(false);
  const inlineCreateNameRef = useRef(null);
  const inlineCreateDescRef = useRef(null);
  const inlineCreateColorRef = useRef(null);
  const inlineCreateRowRef = useRef(null);
  /** Set when moving focus name ↔ description (Tab/Enter) so blur handler skips save */
  const inlineCreateDeferSaveRef = useRef(false);

  // Highlight newly created tag
  const [recentlyCreatedId, setRecentlyCreatedId] = useState(null);

  // Inline color picker
  const [editingColorId, setEditingColorId] = useState(null);
  const colorPickerRef = useRef(null);

  // Selection & bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Delete confirmation modal (zero-usage tags)
  const [deleteConfirmTag, setDeleteConfirmTag] = useState(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Merge modal
  const [showMerge, setShowMerge] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState(null);

  const [merging, setMerging] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(getTags()).then(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!canManage) setSelectedIds(new Set());
  }, [canManage]);

  // Keep selection in sync with the tags that actually exist so a tag removed by
  // delete, merge, or an external refetch can't linger as a ghost id — otherwise
  // the bulk bar count is wrong and Merge can stay enabled with < 2 real tags.
  useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const valid = new Set(tags.map(t => t.id));
      let changed = false;
      const next = new Set();
      prev.forEach(id => {
        if (valid.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [tags]);

  useEffect(() => {
    if (!canManage && sortField === 'usage_count') {
      setSortField('name');
      setSortDir('asc');
    }
  }, [canManage, sortField]);

  useEffect(() => {
    if (editingId !== null && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  useEffect(() => {
    if (editingDescId !== null && editDescInputRef.current) {
      editDescInputRef.current.focus();
      const len = editDescInputRef.current.value.length;
      editDescInputRef.current.setSelectionRange(len, len);
    }
  }, [editingDescId]);

  useEffect(() => {
    if (showInlineCreate && inlineCreateNameRef.current) {
      inlineCreateNameRef.current.focus();
    }
  }, [showInlineCreate]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (!searchWrapperRef.current?.contains(e.target)) setSearchFocused(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (!colorPickerRef.current?.contains(e.target)) setEditingColorId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!inlineCreateColorOpen) return;
    const handleClickOutside = e => {
      if (!inlineCreateColorRef.current?.contains(e.target)) setInlineCreateColorOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inlineCreateColorOpen]);

  // ── Sorting ──────────────────────────────────────────────────────────────

  const toggleSort = field => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIcon = field =>
    sortField === field && sortDir === 'desc' ? ArrowDown01Icon : ArrowUp01Icon;

  const filtered = [...tags]
    .filter(
      t =>
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        (t.description || '').toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => {
      let av;
      let bv;
      if (sortField === 'usage_count') {
        av = Number(a.usage_count ?? 0);
        bv = Number(b.usage_count ?? 0);
      } else if (sortField === 'name') {
        av = (a.name ?? '').toLowerCase();
        bv = (b.name ?? '').toLowerCase();
      } else {
        av = String(a[sortField] ?? '');
        bv = String(b[sortField] ?? '');
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  // ── Selection helpers ─────────────────────────────────────────────────────

  const toggleSelect = id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const allFilteredSelected = filtered.length > 0 && filtered.every(t => prev.has(t.id));
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach(t => next.delete(t.id));
      } else {
        filtered.forEach(t => next.add(t.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectedInFilteredCount = filtered.filter(t => selectedIds.has(t.id)).length;
  const allSelected = filtered.length > 0 && selectedInFilteredCount === filtered.length;
  const someSelected = selectedInFilteredCount > 0 && selectedInFilteredCount < filtered.length;

  const selectedTagObjects = useMemo(
    () => [...selectedIds].map(id => tags.find(t => Number(t.id) === Number(id))).filter(Boolean),
    [selectedIds, tags],
  );
  const bulkDeletableCount = selectedTagObjects.filter(
    t => Number(t.assignment_count ?? 0) === 0,
  ).length;
  const bulkInUseCount = selectedTagObjects.length - bulkDeletableCount;

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleInlineCreateSave = async ev => {
    // Skip save when focus is moving to another element inside the inline create row
    // (e.g. user clicked the description input); only save on true “leave row” blur.
    if (ev?.relatedTarget && inlineCreateRowRef.current?.contains(ev.relatedTarget)) {
      inlineCreateDeferSaveRef.current = false;
      return;
    }
    if (inlineCreateDeferSaveRef.current) {
      inlineCreateDeferSaveRef.current = false;
      return;
    }
    const name = inlineCreateName.trim();
    if (!name) {
      setShowInlineCreate(false);
      setInlineCreateName('');
      setInlineCreateDesc('');
      setInlineCreateColor(DEFAULT_TAG_COLOR);
      setInlineCreateColorOpen(false);
      return;
    }
    if (tags.some(t => t.name.toLowerCase() === name.toLowerCase())) {
      toast.custom(
        toastInstance => (
          <CustomToast
            t={toastInstance}
            title={`"${name}" already exists`}
            message="Choose a different name."
            type="error"
          />
        ),
        { id: 'tag-duplicate-name' },
      );
      setTimeout(() => inlineCreateNameRef.current?.focus(), 10);
      return;
    }
    setInlineCreateSaving(true);
    try {
      const newTag = await dispatch(
        createTag({
          name,
          color: inlineCreateColor,
          description: inlineCreateDesc.trim() || undefined,
        }),
      ).unwrap();
      setShowInlineCreate(false);
      setInlineCreateName('');
      setInlineCreateDesc('');
      setInlineCreateColor(DEFAULT_TAG_COLOR);
      setInlineCreateColorOpen(false);
      // Clear any active filter so the newly created tag is visible in the table
      // (otherwise it may be filtered out and the success highlight never shows).
      setSearch('');
      setSearchFocused(false);
      if (newTag?.id) {
        setRecentlyCreatedId(newTag.id);
        setTimeout(() => setRecentlyCreatedId(null), 2000);
      }
    } catch (e) {
      toast.custom(toastInstance => (
        <CustomToast
          t={toastInstance}
          title="Failed to create tag"
          message={e?.message || 'Please try again.'}
          type="error"
        />
      ));
    } finally {
      setInlineCreateSaving(false);
    }
  };

  const handleInlineCreateCancel = () => {
    inlineCreateDeferSaveRef.current = true;
    setShowInlineCreate(false);
    setInlineCreateName('');
    setInlineCreateDesc('');
    setInlineCreateColor(DEFAULT_TAG_COLOR);
    setInlineCreateColorOpen(false);
  };

  const handleCreateFromSearch = async () => {
    if (!canManage) return;
    const name = search.trim();
    if (!name) return;
    try {
      await dispatch(createTag({ name, color: DEFAULT_TAG_COLOR })).unwrap();
      setSearch('');
      setSearchFocused(false);
    } catch (e) {
      toast.custom(toastInstance => (
        <CustomToast
          t={toastInstance}
          title="Failed to create tag"
          message={e?.message || 'Please try again.'}
          type="error"
        />
      ));
    }
  };

  const handleStartEdit = tag => {
    if (!canManage) return;
    editDeferSaveRef.current = false;
    setEditingId(tag.id);
    setEditingName(tag.name);
  };

  const handleStartEditDesc = tag => {
    if (!canManage) return;
    editDescDeferSaveRef.current = false;
    setEditingDescId(tag.id);
    setEditingDesc(tag.description || '');
  };

  const handleSaveDesc = async tag => {
    if (editDescDeferSaveRef.current) {
      editDescDeferSaveRef.current = false;
      return;
    }
    const newDesc = editingDesc.trim();
    setEditingDescId(null);
    if (newDesc === (tag.description || '').trim()) return;
    try {
      await dispatch(updateTag({ id: tag.id, description: newDesc || null })).unwrap();
    } catch (e) {
      toast.custom(toastInstance => (
        <CustomToast
          t={toastInstance}
          title="Failed to update description"
          message={e?.message || 'Please try again.'}
          type="error"
        />
      ));
    }
  };

  const handleColorChange = async (tag, color) => {
    setEditingColorId(null);
    if (colorEqual(color, tag.color)) return;
    try {
      await dispatch(updateTag({ id: tag.id, color })).unwrap();
    } catch (e) {
      toast.custom(toastInstance => (
        <CustomToast
          t={toastInstance}
          title="Failed to update colour"
          message={e?.message || 'Please try again.'}
          type="error"
        />
      ));
    }
  };

  const handleSaveEdit = async tag => {
    if (editDeferSaveRef.current) {
      editDeferSaveRef.current = false;
      return;
    }
    const newName = editingName.trim();
    setEditingId(null);
    if (!newName || newName === tag.name) return;
    try {
      await dispatch(updateTag({ id: tag.id, name: newName })).unwrap();
    } catch (e) {
      toast.custom(toastInstance => (
        <CustomToast
          t={toastInstance}
          title="Failed to rename tag"
          message={e?.message || 'Please try again.'}
          type="error"
        />
      ));
    }
  };

  const handleDelete = async tag => {
    setLoading(true);
    try {
      await dispatch(deleteTag(tag.id)).unwrap();
      setSelectedIds(prev => {
        if (!prev.has(tag.id)) return prev;
        const next = new Set(prev);
        next.delete(tag.id);
        return next;
      });
    } catch (e) {
      toast.custom(toastInstance => (
        <CustomToast
          t={toastInstance}
          title="Cannot delete tag"
          message={e?.message || 'Remove this tag from all candidates before deleting.'}
          type="error"
        />
      ));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDeleteClick = () => {
    if (bulkDeletableCount === 0) {
      toast.custom(toastInstance => (
        <CustomToast
          t={toastInstance}
          title="No tags can be deleted"
          message="Selected tags are in use. Remove them from candidates first."
          type="warning"
        />
      ));
      return;
    }
    setShowBulkDeleteConfirm(true);
  };

  const handleBulkDelete = async () => {
    setShowBulkDeleteConfirm(false);
    setLoading(true);
    try {
      const res = await dispatch(bulkDeleteTags([...selectedIds])).unwrap();
      clearSelection();
      if (res.result?.skipped > 0) {
        toast.custom(toastInstance => (
          <CustomToast
            t={toastInstance}
            title={`${res.result.skipped} tag${res.result.skipped > 1 ? 's' : ''} skipped`}
            message="Tags in use cannot be deleted. Remove them from candidates first."
            type="warning"
          />
        ));
      }
    } catch (e) {
      toast.custom(toastInstance => (
        <CustomToast
          t={toastInstance}
          title="Failed to delete tags"
          message={e?.message || 'Please try again.'}
          type="error"
        />
      ));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMerge = () => {
    setMergeTargetId(null);
    setShowMerge(true);
  };

  const handleMerge = async () => {
    if (!mergeTargetId) return;
    setMerging(true);
    const targetIdNum = Number(mergeTargetId);
    const sourceIds = [...selectedIds].map(Number).filter(id => id !== targetIdNum);
    try {
      await dispatch(mergeTags({ sourceIds, targetId: targetIdNum })).unwrap();
      setShowMerge(false);
      clearSelection();
      toast.custom(toastInstance => (
        <CustomToast
          t={toastInstance}
          title="Tags merged"
          message="All candidates have been reassigned to the selected tag."
          type="success"
        />
      ));
    } catch (e) {
      toast.custom(toastInstance => (
        <CustomToast
          t={toastInstance}
          title="Failed to merge tags"
          message={e?.message || 'Please try again.'}
          type="error"
        />
      ));
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={cn('card--medium', 'card', 'card-with-border')}>
        {/* Header */}
        <div className="u-padding--large u-divider-bottom-border">
          <div className={cn('u-flex-box', 'u-flex-justify-between', 'u-flex-align-center')}>
            <h1 className="title-2">Tags</h1>
          </div>
          <p className="t-small">
            Tags are used as a way to categorise and discover your content. Add new tags and manage
            existing ones below.
          </p>
        </div>

        {/* Body: grey background like DataProtection */}
        <div className="background-secondary u-padding">
          {/* Controls row */}
          <div
            className={cn(
              'u-flex-box',
              'u-flex-justify-between',
              'u-flex-align-center',
              'u-margin-bottom',
            )}>
            <div className={styles.searchWrapper} ref={searchWrapperRef}>
              <HugeiconsIcon
                icon={Search01Icon}
                size={16}
                strokeWidth={2}
                className={styles.searchIcon}
              />
              <input
                className={styles.searchInput}
                placeholder="Search or Create tags..."
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setSearchFocused(true);
                }}
                onFocus={() => setSearchFocused(true)}
                onClick={() => setSearchFocused(true)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && search.trim() && canManage) {
                    const exact = tags.some(
                      t => t.name.toLowerCase() === search.trim().toLowerCase(),
                    );
                    if (!exact) handleCreateFromSearch();
                  }
                  if (e.key === 'Escape') {
                    setSearch('');
                    setSearchFocused(false);
                  }
                }}
              />
              {searchFocused && search.trim() && (
                <div className={styles.searchDropdown}>
                  {filtered.length > 0 && (
                    <>
                      <div className={styles.dropdownHeader}>Available tags</div>
                      {filtered.slice(0, 6).map(tag => (
                        <div
                          key={tag.id}
                          className={styles.dropdownTagRow}
                          onMouseDown={e => {
                            e.preventDefault();
                            setSearchFocused(false);
                          }}>
                          <span className={styles.colorDot} style={{ '--tag-color': tag.color }} />
                          <div className={styles.dropdownTagInfo}>
                            <span className={styles.dropdownTagName}>{tag.name}</span>
                            {tag.description && (
                              <span className={styles.dropdownTagDesc}>{tag.description}</span>
                            )}
                          </div>
                          {(() => {
                            const n = Number(
                              tag.assignment_count ??
                                Number(tag.usage_count ?? 0) +
                                  Number(tag.archived_usage_count ?? 0),
                            );
                            return n > 0 ? (
                              <span className={styles.dropdownTagUsage}>{n} assigned</span>
                            ) : null;
                          })()}
                        </div>
                      ))}
                    </>
                  )}
                  {canManage &&
                    !tags.some(t => t.name.toLowerCase() === search.trim().toLowerCase()) && (
                      <div
                        className={styles.dropdownCreateRow}
                        onMouseDown={e => {
                          e.preventDefault();
                          handleCreateFromSearch();
                        }}>
                        <div className={styles.dropdownCreateIcon}>+</div>
                        <div>
                          <span className={styles.dropdownCreateLabel}>
                            Create tag &ldquo;{search.trim()}&rdquo;
                          </span>
                          <span className={styles.dropdownCreateHint}>Press Enter to create</span>
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>

            <div className={cn('u-flex-box', 'u-flex-align-center')} style={{ gap: '10px' }}>
              {canManage && (
                <Button
                  onClick={() => {
                    inlineCreateDeferSaveRef.current = false;
                    setShowInlineCreate(true);
                  }}
                  icon="tag">
                  Create Tag
                </Button>
              )}
            </div>
          </div>

          {/* Table — inner card inside grey body */}
          <div className={cn('card', 'card-with-border', styles.tableCard)}>
            {/* Table header */}
            <div
              className={cn(styles.row, styles.header, !canManage && styles.rowWithoutUsageCols)}>
              <div className={styles.checkCell}>
                {canManage ? (
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onClick={() => toggleSelectAll()}
                    style={{ padding: 0 }}
                  />
                ) : null}
              </div>
              <div
                className={cn(styles.nameCell, styles.sortHeader)}
                onClick={() => toggleSort('name')}>
                Name
                {sortField === 'name' ? (
                  <>
                    {' '}
                    <HugeiconsIcon
                      icon={sortIcon('name')}
                      size={16}
                      strokeWidth={2}
                      className={cn(styles.sortIcon, styles.sortIconActive)}
                    />
                  </>
                ) : null}
              </div>
              <div className={styles.headerLabel}>Description</div>
              {canManage ? (
                <>
                  <div
                    className={cn(styles.sortHeader, styles.usageCountHeader)}
                    onClick={() => toggleSort('usage_count')}>
                    Active
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tags-usage-active-tip">Used by active candidates</Tooltip>
                      }>
                      <span
                        className={styles.usageHeaderInfo}
                        tabIndex={0}
                        aria-label="Used by active candidates"
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}>
                        <HugeiconsIcon icon={InformationCircleIcon} size={14} strokeWidth={2} />
                      </span>
                    </OverlayTrigger>
                    {sortField === 'usage_count' ? (
                      <>
                        {' '}
                        <HugeiconsIcon
                          icon={sortIcon('usage_count')}
                          size={16}
                          strokeWidth={2}
                          className={cn(styles.sortIcon, styles.sortIconActive)}
                        />
                      </>
                    ) : null}
                  </div>
                  <div className={cn(styles.usageCountHeaderStatic)}>
                    Archived
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tags-usage-archived-tip">Used by archived candidates</Tooltip>
                      }>
                      <span
                        className={styles.usageHeaderInfo}
                        tabIndex={0}
                        aria-label="Used by archived candidates"
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}>
                        <HugeiconsIcon icon={InformationCircleIcon} size={14} strokeWidth={2} />
                      </span>
                    </OverlayTrigger>
                  </div>
                </>
              ) : null}
              <div className={styles.actionCell} />
            </div>

            {/* Inline create row */}
            {showInlineCreate && (
              <div
                ref={inlineCreateRowRef}
                className={cn(styles.row, styles.inlineCreateRow)}
                style={{ borderLeftColor: inlineCreateColor }}>
                <div className={styles.checkCell} />
                <div className={styles.nameCell}>
                  <div className={styles.colorDotWrapper} ref={inlineCreateColorRef}>
                    <span
                      className={cn(styles.colorDot, styles.colorDotClickable)}
                      style={{ '--tag-color': inlineCreateColor }}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => setInlineCreateColorOpen(v => !v)}
                    />
                    {inlineCreateColorOpen && (
                      <div className={styles.colorPicker} onMouseDown={e => e.preventDefault()}>
                        {TAG_COLOR_PALETTE.map(c => (
                          <button
                            key={c}
                            type="button"
                            className={cn(
                              styles.colorSwatchPicker,
                              colorEqual(inlineCreateColor, c) && styles.colorSwatchPickerActive,
                            )}
                            style={{ '--swatch-color': c }}
                            onClick={() => {
                              setInlineCreateColor(c);
                              setInlineCreateColorOpen(false);
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    ref={inlineCreateNameRef}
                    className={styles.inlineInput}
                    value={inlineCreateName}
                    placeholder="Tag name…"
                    disabled={inlineCreateSaving}
                    onChange={e => setInlineCreateName(e.target.value)}
                    onBlur={e => handleInlineCreateSave(e)}
                    onKeyDown={e => {
                      if (e.key === 'Tab' && !e.shiftKey) {
                        e.preventDefault();
                        inlineCreateDeferSaveRef.current = true;
                        inlineCreateDescRef.current?.focus();
                      }
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        inlineCreateDeferSaveRef.current = true;
                        inlineCreateDescRef.current?.focus();
                      }
                      if (e.key === 'Escape') handleInlineCreateCancel();
                    }}
                  />
                </div>
                <div className={styles.descCell}>
                  <input
                    ref={inlineCreateDescRef}
                    className={styles.inlineInput}
                    value={inlineCreateDesc}
                    placeholder="Add description…"
                    disabled={inlineCreateSaving}
                    onChange={e => setInlineCreateDesc(e.target.value)}
                    onBlur={e => handleInlineCreateSave(e)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                      if (e.key === 'Escape') handleInlineCreateCancel();
                      if (e.key === 'Tab' && e.shiftKey) {
                        e.preventDefault();
                        inlineCreateDeferSaveRef.current = true;
                        inlineCreateNameRef.current?.focus();
                      }
                    }}
                  />
                </div>
                <div className={styles.usageCountCell}>
                  <span>0</span>
                </div>
                <div className={styles.usageCountCell}>
                  <span>0</span>
                </div>
                <div className={styles.actionCell}>
                  <HugeiconsIcon
                    icon={Cancel01Icon}
                    size={16}
                    strokeWidth={2}
                    className={styles.deleteIcon}
                    onMouseDown={e => e.preventDefault()}
                    onClick={handleInlineCreateCancel}
                  />
                </div>
              </div>
            )}

            {/* Tag rows */}
            {filtered.map(tag => (
              <div
                key={tag.id}
                className={cn(
                  styles.row,
                  !canManage && styles.rowWithoutUsageCols,
                  selectedIds.has(tag.id) && styles.rowSelected,
                  (editingId === tag.id || editingDescId === tag.id) && styles.rowEditing,
                  recentlyCreatedId === tag.id && styles.rowHighlight,
                )}
                style={
                  editingId === tag.id || editingDescId === tag.id
                    ? { '--tag-color': tag.color }
                    : undefined
                }>
                {/* Checkbox */}
                <div className={styles.checkCell}>
                  {canManage ? (
                    <Checkbox
                      checked={selectedIds.has(tag.id)}
                      onClick={() => toggleSelect(tag.id)}
                      style={{ padding: 0 }}
                    />
                  ) : null}
                </div>

                {/* Name + colour dot */}
                <div className={styles.nameCell}>
                  <div
                    className={styles.colorDotWrapper}
                    ref={editingColorId === tag.id ? colorPickerRef : null}>
                    <span
                      className={cn(styles.colorDot, canManage && styles.colorDotClickable)}
                      style={{ '--tag-color': tag.color }}
                      onClick={e => {
                        if (!canManage) return;
                        e.stopPropagation();
                        setEditingColorId(prev => (prev === tag.id ? null : tag.id));
                      }}
                    />
                    {editingColorId === tag.id && (
                      <div className={styles.colorPicker}>
                        {TAG_COLOR_PALETTE.map(c => (
                          <button
                            key={c}
                            type="button"
                            className={cn(
                              styles.colorSwatchPicker,
                              colorEqual(tag.color, c) && styles.colorSwatchPickerActive,
                            )}
                            style={{ '--swatch-color': c }}
                            onClick={() => handleColorChange(tag, c)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {editingId === tag.id ? (
                    <input
                      ref={editInputRef}
                      className={styles.inlineInput}
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onBlur={() => handleSaveEdit(tag)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') {
                          editDeferSaveRef.current = true;
                          setEditingId(null);
                        }
                      }}
                    />
                  ) : (
                    <span
                      className={cn(styles.tagName, canManage && styles.tagNameEditable)}
                      onClick={() => handleStartEdit(tag)}>
                      {tag.name}
                    </span>
                  )}
                </div>

                {/* Description */}
                <div className={styles.descCell}>
                  {editingDescId === tag.id ? (
                    <input
                      ref={editDescInputRef}
                      className={styles.inlineInput}
                      value={editingDesc}
                      placeholder="Add a description…"
                      onChange={e => setEditingDesc(e.target.value)}
                      onBlur={() => handleSaveDesc(tag)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') {
                          editDescDeferSaveRef.current = true;
                          setEditingDescId(null);
                        }
                      }}
                    />
                  ) : (
                    <span
                      className={cn(styles.descText, canManage && styles.descTextEditable)}
                      onClick={() => handleStartEditDesc(tag)}>
                      {tag.description ||
                        (canManage ? (
                          <span className={styles.descPlaceholder}>Add description…</span>
                        ) : (
                          ''
                        ))}
                    </span>
                  )}
                </div>

                {canManage ? (
                  <>
                    <div className={styles.usageCountCell}>
                      <span>{Number(tag.usage_count ?? 0)}</span>
                    </div>
                    <div className={styles.usageCountCell}>
                      <span>{Number(tag.archived_usage_count ?? 0)}</span>
                    </div>
                  </>
                ) : null}

                {/* Actions */}
                <div className={styles.actionCell}>
                  {canManage && (
                    <HugeiconsIcon
                      icon={Delete02Icon}
                      size={16}
                      strokeWidth={2}
                      className={styles.deleteIcon}
                      onClick={() => {
                        if (Number(tag.assignment_count ?? 0) > 0) {
                          toast.custom(toastId => (
                            <CustomToast
                              t={toastId}
                              title="Tag is in use"
                              message={tagInUseToastMessage(tag)}
                              type="warning"
                            />
                          ));
                          return;
                        }
                        setDeleteConfirmTag(tag);
                      }}
                    />
                  )}
                </div>
              </div>
            ))}

            {filtered.length === 0 && (
              <div className={styles.empty}>
                {search
                  ? `No tags matching "${search}"`
                  : 'No tags yet. Create your first tag above.'}
              </div>
            )}
          </div>
          {/* end inner table card */}
        </div>
        {/* end background-secondary */}
      </div>
      {/* end outer card */}

      {/* Floating bulk actions bar */}
      <div
        className={cn(
          styles.bulkBar,
          (selectedIds.size === 0 || !canManage) && styles.bulkBarHidden,
          'u-drop-shadow--large',
        )}>
        <span className={styles.bulkBadge}>{selectedIds.size} Selected</span>
        <div className={styles.bulkDivider} />
        <div className={styles.bulkActions}>
          <IconComponent
            icon={GitMergeIcon}
            type="neutral-light"
            noBackground
            tip="Merge tags"
            onClick={handleOpenMerge}
            disabled={selectedIds.size < 2}
          />
          <IconComponent
            icon={Delete02Icon}
            type="neutral-light"
            noBackground
            tip="Delete tags"
            onClick={handleBulkDeleteClick}
            disabled={bulkDeletableCount === 0}
          />
        </div>
        <IconButton icon="x" onClick={clearSelection} />
      </div>

      {/* Single-tag delete confirmation (only offered for zero-usage tags) */}
      <Alert
        show={!!deleteConfirmTag}
        title={`Are you sure you want to delete "${deleteConfirmTag?.name}"?`}
        message="Deletion cannot be undone. Are you sure you want to continue?"
        ok="Delete"
        cancel="Cancel"
        type="error"
        icon={Delete02Icon}
        onOk={async () => {
          const tag = deleteConfirmTag;
          setDeleteConfirmTag(null);
          await handleDelete(tag);
        }}
        onCancel={() => setDeleteConfirmTag(null)}
      />

      {/* Bulk delete confirmation */}
      <Alert
        show={showBulkDeleteConfirm}
        title={bulkDeletableCount === 1 ? 'Delete 1 tag?' : `Delete ${bulkDeletableCount} tags?`}
        message={
          bulkInUseCount > 0
            ? `${bulkInUseCount} selected tag${bulkInUseCount !== 1 ? 's are' : ' is'} in use and will be skipped. Only unused tags will be removed. This cannot be undone.`
            : 'This will permanently remove the selected unused tags. This cannot be undone.'
        }
        ok="Delete"
        cancel="Cancel"
        type="error"
        icon={Delete02Icon}
        onOk={handleBulkDelete}
        onCancel={() => setShowBulkDeleteConfirm(false)}
      />

      {/* Merge modal */}
      <Modal show={showMerge} onClose={() => setShowMerge(false)}>
        <div className={styles.modalContent}>
          <h2 className="title-3">Merge Tags</h2>
          <p className="t-small u-margin-bottom">
            Select the tag to keep. All candidates from the other tags will be reassigned to it, and
            the other tags will be deleted.
          </p>

          <div className={styles.mergeList}>
            {[...selectedIds].map(id => {
              const numId = Number(id);
              const tag = tags.find(t => t.id === numId);
              if (!tag) return null;
              return (
                <label
                  key={numId}
                  className={cn(
                    styles.mergeOption,
                    Number(mergeTargetId) === numId && styles.mergeOptionSelected,
                  )}>
                  <input
                    type="radio"
                    name="mergeTarget"
                    value={numId}
                    checked={Number(mergeTargetId) === numId}
                    onChange={() => setMergeTargetId(numId)}
                    className={styles.mergeRadio}
                  />
                  <span className={styles.colorDot} style={{ '--tag-color': tag.color }} />
                  <span className={styles.mergeName}>{tag.name}</span>
                  <span className={styles.usageCount}>
                    {tag.assignment_count} candidate{Number(tag.assignment_count) !== 1 ? 's' : ''}
                  </span>
                </label>
              );
            })}
          </div>

          <div
            className={cn('u-flex-box', 'u-flex-justify-end', 'u-margin-top')}
            style={{ gap: '10px' }}>
            <Button type="outline" onClick={() => setShowMerge(false)}>
              Cancel
            </Button>
            <Button onClick={handleMerge} disabled={!mergeTargetId || merging}>
              {merging ? 'Merging...' : 'Merge Tags'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
